import { Box } from "@mui/material";
import { useCallback, useReducer, useRef } from "react"
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import useDialogModal from "hooks/useDialogModal";
import { formatDateWithPreferences } from "utils/dateFormatUtils";
import { usePreferences } from "hooks/usePreferences";
import validLookup from "utils/validLookup";
import AddRxDialog from "./addRxDialog";
import EditRxDialog from "./editRxDialog";
import { RxService } from "../../../../service/rxService";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import { toast } from "react-toastify";
import useAbortController from "hooks/useAbortController";
import { isLocalDev } from "utils/environment";
import AddTaskDialog from "../../../task/AddTaskDialog";

const PrescriptionColumns = ({ preferences }) => [
    {
        Header: 'Rx Date',
        accessor: 'rxDate',
        enableFilter: true,
        type: 'date',
        Cell: ({ value }) => formatDateWithPreferences(value, preferences)
    },
    {
        Header: 'Expiry',
        accessor: 'expiryDate',
        enableFilter: true,
        type: 'date',        
        Cell: ({ value }) => formatDateWithPreferences(value, preferences)
    },    
    {
        Header: 'Type',
        accessor: 'rxType',
        enableFilter: true,
        type: 'enum',
        filterOptions: [{value: 'glasses', label: "Glasses"}, {value: 'contacts', label: 'Contacts'}]
    },
    {
        Header: 'Source',
        accessor: 'practitionerSource',
        enableFilter: true,
        type: 'enum',
        filterOptions: [{value: 'internal', label: "Internal"}, {value: 'external', label: 'External'}],
        Cell: ({ value }) => value === 'internal' ? 'Internal' : 'External'
    },
    {
        Header: 'Doctor',
        accessor: 'practitioner.fullName',
        Cell: ({ row, value }) => value || row.original.externalPractitionerName || ''
    },
    {
        Header: 'Location',
        accessor: 'location.name',
        enableFilter: false
    }
];

export default function RxTab({ customer }) {
    const { preferences } = usePreferences();
    const [signal] = useAbortController();
    const lastQueryRef = useRef(null);
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params }
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        loading: false,
        showRxDialog: false,
        showEditDialog: false,
        selectedPrescription: null,
        showTaskDialog: false,
        taskTarget: null
    });

    const [ConfirmationDialogComp, showConfirmationDialog, closeConfirmationDialog] =
        useDialogModal(ConfirmationDialog);

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        lastQueryRef.current = { pageSize, pageIndex, filters, sortBy };
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        RxService.getWithLimit(from, pageSize, filters, sortBy, customer.customer.id, signal)
            .then(result => {
                const { code, data } = result;
                if (code === 0) {
                    updateState({
                        data: data.data,
                        totalRowCount: data.count,
                        pageCount: Math.ceil(data.count / pageSize)
                    });
                }
                updateState({ loading: false });
            })
            .catch(error => {
                if (isLocalDev) {
                    console.error(error);
                }
                updateState({ loading: false });
            });
    }, [customer.customer.id, signal]);

    const refreshRxList = useCallback(() => {
        if (lastQueryRef.current) {
            fetchData(lastQueryRef.current);
        }
    }, [fetchData]);

    const handleDeleteClicked = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                try {
                    const deleteResult = await RxService.deletePrescription(rowids.map(x => parseInt(x, 10)));
                    const { code, message } = deleteResult;
                    if (code === 0) {
                        toast.success(message);
                        const updatedList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                        updateState({ data: updatedList });
                    } else {
                        toast.error("Failed to delete record(s)");
                    }
                } catch (error) {
                    toast.error("Failed to delete record(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} rx(s)?`
        });
    };

    const handleRowEdit = (rowData) => {
        updateState({ selectedPrescription: rowData, showEditDialog: true });
    };

    const handleRxCreated = () => {
        refreshRxList();
        updateState({ showRxDialog: false });
    };

    const handleRxUpdated = () => {
        refreshRxList();
        updateState({ showEditDialog: false, selectedPrescription: null });
    };

    const handleAddTask = (rowData) => {
        updateState({ taskTarget: rowData, showTaskDialog: true });
    };

    const prescriptionColumns = PrescriptionColumns({ preferences });

    return (
        <Box display="flex" flexDirection="column">
            <EnhancedTable
                columns={prescriptionColumns}
                addRecordHandler={() => updateState({ showRxDialog: true })}
                deleteRecordHandler={handleDeleteClicked}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                actions={[
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                    {
                        icon: <PlaylistAddCheckIcon />,
                        onClick: handleAddTask
                    }
                ]}
            />
            <AddRxDialog 
                open={state.showRxDialog} 
                onClose={() => updateState({ showRxDialog: false })} 
                customer={customer} 
                onCreated={handleRxCreated}
            />
            <EditRxDialog 
                open={state.showEditDialog} 
                onClose={() => updateState({ showEditDialog: false, selectedPrescription: null })} 
                customer={customer}
                prescriptionData={state.selectedPrescription}
                onUpdated={handleRxUpdated}
            />
            <AddTaskDialog
                open={state.showTaskDialog}
                onClose={() => updateState({ showTaskDialog: false, taskTarget: null })}
                entityType="rx"
                entityId={state.taskTarget?.id || null}
                onTaskCreated={() => updateState({ showTaskDialog: false })}
            />
            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel"
                }}
            />
        </Box>
    )
}